import { SectionLayoutType } from '../../../../../../../../../models/enum/dto/section-layout-type.enum';
import { ColWidth } from '../../../../../../../../../models/shared/col-width';
import { SectionColumnConfig } from '../../../../../../../../../models/menu/section/section-column-config';
import { SectionColumnConfigState } from '../../../../../../../../../models/enum/shared/section-column-config-state';
import { SectionColumnConfigKey } from '../../../../../../../../../models/enum/shared/section-column-config-key';
import { SectionColumnConfigProductInfoKey } from '../../../../../../../../../models/enum/shared/section-column-config-product-info-key';
import { SectionColumnConfigCannabinoidKey } from '../../../../../../../../../models/enum/shared/section-column-config-cannabinoid-key';
import {
  SectionColumnConfigDataBadgeMode,
  SectionColumnConfigDataValue
} from '../../../../../../../../../models/enum/shared/section-column-config-data-value';
import { SectionColumnCannabinoidType } from '../../../../../../../../../models/enum/shared/section-column-cannabinoid-type';
import { SectionColumnType } from '../../../../../../../../../models/enum/shared/section-column-type';
import { SectionColumnTerpeneType } from '../../../../../../../../../models/enum/shared/section-column-terpene-type';
import { SectionColumnProductInfoType } from '../../../../../../../../../models/enum/shared/section-column-product-info-type';
import { TagGroup_Prefix } from '../../../../../../../../../models/enum/shared/section-theme-tag-group-keys';

export class SectionColumnViewModel {
  constructor(
    public sectionLayoutType: SectionLayoutType,
    public columnConfig: SectionColumnConfig
  ) {}

  /* ****************************** Column Ordering ****************************** */

  static getDefaultAdditionalDetailsColumnOrder(startingNumber: number): [SectionColumnType, number][] {
    const getOrderNumber = (index: number) => startingNumber + 0.001 * index;
    return [
      [SectionColumnProductInfoType.HelpsWith, getOrderNumber(1)],
      [SectionColumnProductInfoType.Effects, getOrderNumber(2)],
      [SectionColumnProductInfoType.Aroma, getOrderNumber(3)],
      [SectionColumnProductInfoType.GrowingConditions, getOrderNumber(4)],
      [SectionColumnProductInfoType.CraftDesignation, getOrderNumber(5)],
      [SectionColumnProductInfoType.Appearance, getOrderNumber(6)],
      [SectionColumnProductInfoType.Flavor, getOrderNumber(7)],
      [SectionColumnProductInfoType.TopFlavor, getOrderNumber(8)],
      [SectionColumnProductInfoType.Lineage, getOrderNumber(9)],
      [SectionColumnProductInfoType.EnergyScore, getOrderNumber(10)]
    ];
  }

  static getDefaultCannabinoidColumnOrdering(
    startingNumber: number,
    includeTHCMixedWithCBDColumn: boolean = true
  ): [keyof typeof SectionColumnCannabinoidType, number][] {
    const getOrderNumber = (index: number) => startingNumber + 0.001 * index;
    const filterOutPrimary = (it: keyof typeof SectionColumnCannabinoidType) => {
      return (
        it !== SectionColumnCannabinoidType.THC &&
        it !== SectionColumnCannabinoidType.CBD &&
        it !== SectionColumnCannabinoidType.THCAndCBD
      );
    };
    const secondaryCannabinoids = Object.values(SectionColumnCannabinoidType).filter(filterOutPrimary);
    return [
      [SectionColumnCannabinoidType.THC, getOrderNumber(0)],
      [SectionColumnCannabinoidType.CBD, getOrderNumber(1)],
      ...(includeTHCMixedWithCBDColumn
        ? [
            [SectionColumnCannabinoidType.THCAndCBD, getOrderNumber(2)] as [
              keyof typeof SectionColumnCannabinoidType,
              number
            ]
          ]
        : []),
      ...secondaryCannabinoids.map(
        (c, i) => [c, getOrderNumber(i + 3)] as [keyof typeof SectionColumnCannabinoidType, number]
      )
    ];
  }

  static getDefaultTerpeneColumnOrdering(startingNumber: number): [SectionColumnType, number][] {
    const getOrderNumber = (index: number) => startingNumber + 0.001 * index;
    return Object.values(SectionColumnTerpeneType).map((c, i) => [c, getOrderNumber(i)] as [SectionColumnType, number]);
  }

  static getDefaultProductTagGroupColumnOrdering(startingNumber: number): [SectionColumnType, number] {
    return [TagGroup_Prefix, startingNumber];
  }

  /* ****************************** Column Builders ****************************** */

  static getProductTitleColumn(
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>
  ): SectionColumnViewModel {
    const productTitleCol = new SectionColumnViewModel(sectionLayoutType, null);
    const type = SectionColumnProductInfoType.ProductTitle;
    productTitleCol.columnType = type;
    productTitleCol.state = SectionColumnConfigState.On;
    productTitleCol.position = columnOrdering?.get(type);
    return productTitleCol;
  }

  private static getColumn(
    columnConfigKey: SectionColumnConfigKey,
    type: SectionColumnType,
    colConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const columnConfig = colConfigMap?.get(columnConfigKey);
    const column = new SectionColumnViewModel(sectionLayoutType, columnConfig);
    column.columnTitle = colConfigMap?.get(columnConfigKey)?.columnName;
    column.columnType = type;
    column.position = columnOrdering?.get(type);
    column.state = colConfigMap?.get(columnConfigKey)?.defaultState;
    column.widthPercentage = widths.find(it => it.type === type)?.widthPercentage;
    return column;
  }

  static getAssetColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.Asset;
    const type = SectionColumnProductInfoType.Asset;
    return SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
  }

  static getBadgeColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.Badges;
    const type = SectionColumnProductInfoType.Badge;
    return SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
  }

  static getBrandColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.Brand;
    const type = SectionColumnProductInfoType.Brand;
    return SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
  }

  static getStrainColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.Strain;
    const type = SectionColumnProductInfoType.Strain;
    return SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
  }

  static getStrainClassColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.StrainType;
    const type = SectionColumnProductInfoType.StrainType;
    const column = SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
    column.strainTypeMode = colConfig?.get(key)?.dataValue;
    column.data = column.strainTypeMode;
    return column;
  }

  static getLineageColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.Lineage;
    const type = SectionColumnProductInfoType.Lineage;
    return SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
  }

  static getCannabinoidColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[],
    key: keyof typeof SectionColumnConfigCannabinoidKey,
    type: keyof typeof SectionColumnCannabinoidType
  ): SectionColumnViewModel {
    const column = SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
    column.data = colConfig?.get(key)?.dataValue;
    return column;
  }

  static getTerpeneColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[],
    key: SectionColumnConfigKey,
    type: SectionColumnType
  ): SectionColumnViewModel {
    const column = SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
    column.data = colConfig?.get(key)?.dataValue;
    return column;
  }

  static getProductTagGroupColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[],
    key: SectionColumnConfigKey,
    type: SectionColumnType
  ): SectionColumnViewModel {
    const column = SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
    column.position = columnOrdering?.get(TagGroup_Prefix);
    column.data = colConfig?.get(key)?.dataValue;
    return column;
  }

  static getQuantityAndSizeColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.QuantityAndSize;
    const type = SectionColumnProductInfoType.QuantityAndSize;
    return SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
  }

  static getQuantityColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.Quantity;
    const type = SectionColumnProductInfoType.Quantity;
    return SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
  }

  static getStockColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.Stock;
    const type = SectionColumnProductInfoType.Stock;
    return SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
  }

  static getVariantTypeColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.VariantType;
    const type = SectionColumnProductInfoType.VariantType;
    const column = SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
    column.data = colConfig?.get(key)?.dataValue;
    return column;
  }

  static getSizeColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.Size;
    const type = SectionColumnProductInfoType.Size;
    return SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
  }

  static getReviewResultsColumn(
    colConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.GradingResults;
    const type = SectionColumnProductInfoType.GradingResults;
    const column = SectionColumnViewModel.getColumn(key, type, colConfigMap, sectionLayoutType, columnOrdering, widths);
    column.data = colConfigMap?.get(key)?.dataValue;
    return column;
  }

  static getPriceColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.Price;
    const type = SectionColumnProductInfoType.VariantPrice;
    return SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
  }

  static getSecondaryPriceColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.SecondaryPrice;
    const type = SectionColumnProductInfoType.VariantSecondaryPrice;
    const column = SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
    column.secondaryPriceMode = colConfig?.get(key)?.dataValue;
    return column;
  }

  static getHelpsWithColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.HelpsWith;
    const type = SectionColumnProductInfoType.HelpsWith;
    const column = SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
    column.data = colConfig?.get(key)?.dataValue;
    return column;
  }

  static getEffectsColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.Effects;
    const type = SectionColumnProductInfoType.Effects;
    const column = SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
    column.data = colConfig?.get(key)?.dataValue;
    return column;
  }

  static getAromaColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.Aroma;
    const type = SectionColumnProductInfoType.Aroma;
    const column = SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
    column.data = colConfig?.get(key)?.dataValue;
    return column;
  }

  static getGrowingConditionsColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.GrowingConditions;
    const type = SectionColumnProductInfoType.GrowingConditions;
    const column = SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
    column.data = colConfig?.get(key)?.dataValue;
    return column;
  }

  static getCraftDesignationColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.CraftDesignation;
    const type = SectionColumnProductInfoType.CraftDesignation;
    const column = SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
    column.data = colConfig?.get(key)?.dataValue;
    return column;
  }

  static getAppearanceColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.Appearance;
    const type = SectionColumnProductInfoType.Appearance;
    const column = SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
    column.data = colConfig?.get(key)?.dataValue;
    return column;
  }

  static getFlavorColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.Flavor;
    const type = SectionColumnProductInfoType.Flavor;
    const column = SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
    column.data = colConfig?.get(key)?.dataValue;
    return column;
  }

  static getTopFlavorColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.TopFlavor;
    const type = SectionColumnProductInfoType.TopFlavor;
    const column = SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
    column.data = colConfig?.get(key)?.dataValue;
    return column;
  }

  static getEnergyScoreColumn(
    colConfig: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    sectionLayoutType: SectionLayoutType,
    columnOrdering: Map<SectionColumnType, number>,
    widths: ColWidth[]
  ): SectionColumnViewModel {
    const key = SectionColumnConfigProductInfoKey.EnergyScore;
    const type = SectionColumnProductInfoType.EnergyScore;
    const column = SectionColumnViewModel.getColumn(key, type, colConfig, sectionLayoutType, columnOrdering, widths);
    column.data = colConfig?.get(key)?.dataValue;
    return column;
  }

  /* *************************************************************************** */

  state: SectionColumnConfigState;
  position: number;
  columnTitle: string;
  columnType: SectionColumnType;
  widthPercentage: string;
  secondaryPriceMode: SectionColumnConfigDataValue;
  data: SectionColumnConfigDataValue;
  strainTypeMode: SectionColumnConfigDataValue;

  public getPriceLongFormatColumnTitle(): string {
    if (this.sectionLayoutType === SectionLayoutType.Grid) {
      switch (this.columnTitle) {
        case '1 pk':
          return 'Single';
        /*
         * Regex explanation: https://regex101.com/
         * \b assert position at a word boundary: (^\w|\w$|\W\w|\w\W)
         * pk matches the characters' pk literally (case sensitive)
         * \b assert position at a word boundary: (^\w|\w$|\W\w|\w\W)
         *
         * Outcome: matches the WORD 'pk', and not the character sequence 'pk'.
         * NOT captured: "pumpkin" || "napkin" || "upkeep" || ...etc
         * CAPTURED: "2 pk" || "3 per pk" || "pk" || "per pk" || ...etc
         */
        default:
          return this.columnTitle.replace(/\bpk\b/, 'pack');
      }
    }
    if (this.sectionLayoutType === SectionLayoutType.ClassicFlowerGrid) {
      switch (this.columnTitle) {
        case '1 g':
          return '1 g';
        case '3.5 g':
          return '1/8 oz';
        case '7 g':
          return '1/4 oz';
        case '14 g':
          return '1/2 oz';
        case '28 g':
          return '1 oz';
      }
    }
    return this.columnTitle;
  }

  public isAssetColumn(): boolean {
    return this.columnType === SectionColumnProductInfoType.Asset;
  }

  public isBadgeColumn(): boolean {
    return this.columnType === SectionColumnProductInfoType.Badge;
  }

  public columnConfigDataValueIsBadgeMode(): boolean {
    return this.data === SectionColumnConfigDataBadgeMode.Badge;
  }

  public uniqueId(): string {
    return `
      -${this.state}
      -${this.position}
      -${this.columnTitle}
      -${this.columnType}
      -${this.widthPercentage}
      -${this.secondaryPriceMode}
      -${this.data}
      -${this.strainTypeMode}
    `;
  }
}
